import dayjs from "dayjs";
import React, { Component } from "react";
import { reportException } from "./utils/sentry";

// Lazy: keeps googleAnalytics (and its storageService/utils graph) out of the
// entry chunk. Crash events are rare; the module arrives well before the
// user can do anything else, and dataLayer queues the event regardless.
const fireAnalyticsEvent = (...args) => {
  import("./utils/googleAnalytics").then(({ fireGtmEvent }) =>
    fireGtmEvent(...args),
  );
};

class ErrorBoundry extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    fireAnalyticsEvent("page_crashed", {
      error,
      info,
      errMsg: error?.message,
      description: error?.message || String(error),
    });
    reportException(error, info);
    console.error("ErrorBoundary caught an error", error, info);

    // Skip auto-reload for bots/prerender
    if (typeof window !== 'undefined' && window.navigator && /bot|crawler|spider|prerender/i.test(window.navigator.userAgent)) {
      this.setState({ hasError: true });
      return;
    }

    // Skip auto-reload on checkout or booking routes so we do not destroy Redux cart state or trap users in loops
    const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
    const isCheckoutOrBooking = pathname.includes('/checkout') || pathname.includes('/bookings/');

    let shouldReload = false;
    if (!isCheckoutOrBooking) {
      try {
        const lastReloaded = localStorage.getItem("last_error_reloaded");
        if (!lastReloaded || dayjs(lastReloaded).isBefore(dayjs().subtract(10, "minutes"))) {
          const now = dayjs().toISOString();
          localStorage.setItem("last_error_reloaded", now);
          // If storage is blocked, show recovery instead of reloading forever.
          shouldReload = localStorage.getItem("last_error_reloaded") === now;
        }
      } catch (_) { /* Keep the recovery page available without storage. */ }
    }
    if (shouldReload) this.handleReload();
    this.setState({
      hasError: true,
    });
  }

  handleReload = async () => {
    fireAnalyticsEvent("errorboundary_reloaded");

    // 1. Flush Service Worker / Workbox caches
    if (typeof caches !== "undefined" && caches.keys) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch (_) { /* Ignore cache deletion errors */ }
    }

    // 2. Unregister stale Service Worker to ensure fresh network fetch
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      } catch (_) { /* Ignore service worker unregistration errors */ }
    }

    // 3. Retain the guard across automatic and manual reloads. Clearing it
    // here would cause every recurring render error to reload indefinitely.
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem("last_error_reloaded", dayjs().toISOString());
      } catch (_) { /* Ignore storage write errors */ }
    }

    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // For bots, render empty div to allow React Helmet meta tags to stay
      const isBot = typeof window !== 'undefined' && window.navigator && /bot|crawler|spider|prerender/i.test(window.navigator.userAgent);

      if (isBot) {
        // Return minimal content for bots - React Helmet tags will still be in head
        return (
          <div style={{padding: '20px'}}>
            <h1>Loading...</h1>
            <p>Please wait while we load the content.</p>
          </div>
        );
      }

      // You can render any custom fallback UI for regular users
      return (
        <div className="errorBoundary">
          <h1>Something went wrong.</h1>
          <p>
            We're sorry for the inconvenience. Please try refreshing the page or
            reopening the tab.
          </p>
          <button onClick={this.handleReload}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

export const SentryErrorBoundary = ({ children }) => {
  return <ErrorBoundry>{children}</ErrorBoundry>;
};

export default ErrorBoundry;
